<template>
  <v-app>
    <group-card title="Journeys" icon="list_alt">
      <template slot="actions">
        <loading-icon-button
          @click="getJourneys"
          tooltip="Refresh"
          :loading="isLoading"
        >
          cached
        </loading-icon-button>
        <md-button
          class="md-primary md-sm"
          @click="createNewJourney"
        >
          Add new Journey
          <md-tooltip md-direction="top">Add New Journey</md-tooltip>
        </md-button>
      </template>
      <v-data-table
        class="align-table"
        :headers="headers"
        :items="journeys"
        sort-by="id"
      >
        <template v-slot:item.default="{ item }">
          <v-switch
            color="primary"
            v-model="item.default"
            @change="setDefault(item)"
            :disabled="item.default"
            dense
          />
        </template>
        <template v-slot:item.actions="{ item }">
          <md-button
            class="md-primary md-sm"
            @click="createDuplicateJourney(item)"
          >
            Duplicate
            <md-tooltip md-direction="top">Duplicate Journey</md-tooltip>
          </md-button>
          <md-button
            class="md-primary md-sm"
            @click="editJourney(item)"
          >
            Edit
            <md-tooltip md-direction="top">Edit Journey</md-tooltip>
          </md-button>
          <md-button
            class="md-danger md-sm"
            @click="deleteJourney(item)"
          >
            Delete
            <md-tooltip md-direction="top">Delete Journey</md-tooltip>
          </md-button>
        </template>
      </v-data-table>
    </group-card>
  </v-app>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { GroupCard } from "@/components";
import ConfirmDialog from '@/components/Dialogs/ConfirmDialog.vue';
import WaitingDialog from '@/components/Dialogs/WaitingDialog.vue';
import LoadingIconButton from '@/components/LoadingIconButton';

export default {
  components: {
    GroupCard,
    ConfirmDialog,
    WaitingDialog,
    LoadingIconButton,
  },
  data () {
    return {
      headers: [
        { text: 'ID', value: 'id', align: 'start' },
        { text: 'Name', value: 'name' },
        { text: 'Is Default', value: 'default' },
        { text: 'Actions', value: 'actions', sortable: false, width: '20%' },
      ],
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('journey', [
      "journeys",
    ])
  },
  methods: {
    ...mapActions('journey', [
      'fetchJourneys',
      'fetchJourney',
      'fetchSteps',
      'saveJourneyAsDefault',
      'duplicateJourney',
    ]),
    ...mapActions('journey', {
      deleteJourneyAction: 'deleteJourney',
    }),
    ...mapMutations('journey', ['setIsCreateFromDefault']),
    editJourney (item) {
      this.$router.push(`/administration/journey/${item.id}/edit`);
    },
    async createNewJourney () {
      if (await this.isCreateFromDefault()) {
        this.$modal.show(WaitingDialog, {
          title: "Setting Journey...",
          message: "Please wait while the default journey is being set."
        });
        const defaultJourney = this.journeys.find( journey => journey.default );
        if (!defaultJourney) {
          this.$modal.submit();
          this.$modal.show(ConfirmDialog, {
            title: "Journey Creation Failed",
            message: "Cannot find default Journey.",
            confirmText: "Confirm",
          });
          return;
        }
        try {
          await this.fetchJourney(defaultJourney.id);
          this.setIsCreateFromDefault(true);
        } catch (error) {
          this.$modal.submit();
          this.$modal.show(ConfirmDialog, {
            title: "Cannot create journey from default template",
            message: error,
            confirmText: "Confirm",
          })
          return;
        }
        this.$modal.submit();
        this.$router.push('/administration/journey/new/edit');
      } else {
        this.$router.push('/administration/journey/new/edit');
      }
    },
    async isCreateFromDefault () {
      return this.$modal.show(ConfirmDialog, {
        title: "Create Journey From Default",
        message: "Do you want to create the journey from the default template?",
        confirmText: "Yes",
        cancelText: "No",
      })
    },
    async setDefault (item) {
      if (await this.setDefaultDialog(item)) {
        this.isLoading = true;
        try {
          await this.saveJourneyAsDefault(item.id);
          await this.fetchJourneys();
          this.$modal.show(ConfirmDialog, {
            title: "Successful",
            message: `${item.name} has been set as default journey.`,
            confirmText: "Confirm",
          });
          this.isLoading = false;
          return;
        } catch (error) {
          this.$modal.show(ConfirmDialog, {
            title: "Unable to set the journey as default",
            message: error,
            confirmText: "Confirm",
          });
        }
      }
      this.isLoading = false;
      item.default = false;
    },
    async setDefaultDialog (item) {
      return this.$modal.show(ConfirmDialog, {
        title: "Set Journey as Default",
        message: `Do you want to set ${item.name} as default journey?`,
        confirmText: "Yes",
        cancelText: "No",
      });
    },
    async getJourneys () {
      try {
        this.isLoading = true;
        await this.fetchJourneys();
        await this.fetchSteps();
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$modal.show(ConfirmDialog, {
          title: "Unable to get Journeys",
          message: error,
          confirmText: "Confirm",
        })
      }
    },
    async createDuplicateJourney (item) {
      console.log(item);
      if (await this.isCreateFromDuplicate(item)) {
        this.$modal.show(WaitingDialog, {
          title: "Duplicating Journey...",
          message: `Please wait while ${item.name} is being duplicated.`
        });
        try {
          const newJourney = await this.duplicateJourney(item.id);
          // redirect after created
          await this.$modal.show(ConfirmDialog, {
            title: "Journey Duplication Successful",
            message: `Duplicate ${item.name} successfully, click confirm to redirct to new journey.`,
            confirmText: "Confirm",
          });
          this.$router.push(`/administration/journey/${newJourney.id}/edit`);
        } catch (error) {
          this.$modal.submit();
          this.$modal.show(ConfirmDialog, {
            title: `Unable to duplicate ${item.name}`,
            message: error,
            confirmText: "Confirm",
          })
        }
      }
    },
    async isCreateFromDuplicate (item) {
      return this.$modal.show(ConfirmDialog, {
        title: "Duplicate Journey",
        message: `Do you want to duplicate ${item.name}?`,
        confirmText: "Yes",
        cancelText: "No",
      })
    },
    async deleteJourney (item) {
      if (await this.isDeleteJourney(item)) {
        this.$modal.show(WaitingDialog, {
          title: "Deleting Journey",
          messge: "Please wait whilst journey is being deleted..."
        });
        try {
          await this.deleteJourneyAction(item.id);
          this.$modal.submit();
          this.$modal.show(ConfirmDialog, {
            title: "Journey Deletion Successful",
            message: `Journey ${item.name} has been deleted from Zink.`,
            confirmText: "Confirm",
          });
        } catch (error) {
          this.$modal.submit();
          this.$modal.show(ConfirmDialog, {
            title: "Delete Journey Failed",
            message: error,
            confirmText: "Confirm",
          });
        }
        await this.getJourneys();
      }
    },
    async isDeleteJourney (item) {
      return this.$modal.show(ConfirmDialog, {
        title: "Delete Journey",
        message: `Journey ${item.name} will be deleted from Zink.`,
        confirmText: "Yes",
        cancelText: "No",
        confirmButtonColor: 'md-danger'
      });
    }
  },
  async mounted () {
    await this.getJourneys();
  }
}
</script>
<style lang="scss" scoped>
.align-table::v-deep th,
.align-table::v-deep td {
  text-align: left; /* Align the content to the center */
}
</style>